import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserType } from '../../utils/userUtils';
import { trackEventWithUTM } from '../../utils/analytics';
import ProviderDashboard from '../ProviderDashboard';
import AdminDashboard from '../AdminDashboard';
import ProviderUnderReview from '../ProviderUnderReview';
import HeroBanner from '../HeroBanner';
import HowItWorks from '../HowItWorks';
import IntentSelector from '../IntentSelector';
import KashiPujas from '../KashiPujas';
import PopularPujas from '../PopularPujas';
import ServiceCards from '../ServiceCards';
import YouTubeSection, { YouTubeVideo } from '../YouTubeSection';
import SEOHead from '../SEOHead';

/**
 * Smart Landing Page Component
 * 
 * This component automatically determines which landing page to show based on:
 * 1. User authentication status
 * 2. User roles (customer vs provider)
 * 
 * Routing Logic:
 * - Unauthenticated users: Show public homepage (ImageCarousel + ServiceCards)
 * - Authenticated customers: Show public homepage (ImageCarousel + ServiceCards)
 * - Authenticated providers: Show ProviderDashboard
 * - Authenticated admins: Show ProviderDashboard (with admin features)
 */
// ─── YouTube Video Data ─────────────────────────────────────────────────────
// Replace the videoId values with your actual YouTube video IDs.
// The ID is the part after ?v= in the YouTube URL.
// Example: https://www.youtube.com/watch?v=dQw4w9WgXcQ → videoId: 'dQw4w9WgXcQ'
const homePageVideos: YouTubeVideo[] = [
  {
    videoId: '6128VDrIiNQ',
    caption: 'Shanti Path Pujan – Performed at Home for Peace & Harmony',
  },
  {
    videoId: 'kXm7GHrOB2k',
    caption: 'Customer Speaks About Their Puja Experience – Real Stories from Real Families',
  },
  {
    videoId: '3TxLb11r4yI',
    caption: 'What our Acharyas Say – About Antaryatra & the Importance of Vedic Rituals',
  },
];

const SmartLanding: React.FC = () => {
  const { user, isAuthenticated, isProviderUnderReview, isProvider } = useAuth();
  const location = useLocation();

  // Handle scroll to services if requested from navigation state
  useEffect(() => {
    const state = location.state as { scrollToServices?: boolean } | null;
    if (state?.scrollToServices) {
      // Small delay to ensure the page has fully loaded
      setTimeout(() => {
        const servicesElement = document.getElementById('services');
        if (servicesElement) {
          servicesElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 500);
    } else {
      // Normal scroll to top behavior
      window.scrollTo(0, 0);
    }
  }, [location]);

  // If user is not authenticated, show the public homepage
  if (!isAuthenticated || !user) {
    return (
      <>
        <SEOHead
          title="Book Kashi Acharyas for Pooja at Home & Kashi Rituals"
          description="Book Kashi-certified Acharyas for authentic Vedic puja at home in Delhi NCR — or have your ritual performed at our sacred Kashi site. Griha Pravesh, Marriage, Dosha Nivaran & more."
          canonicalPath="/"
          keywords="Kashi Acharya booking, book pandit at home Delhi NCR, Griha Pravesh puja pandit, pandit for marriage puja, Dosha Nivaran puja, Rudrabhishekh Kashi, NRI puja Kashi, Kaal Sarp Dosh Nivaran, Shradh Karm Kashi, online puja booking"
          structuredData={[
            {
              "@context": "https://schema.org",
              "@type": ["LocalBusiness", "ReligiousOrganization"],
              "name": "Antaryatra",
              "description": "Book Kashi-certified Acharyas for authentic Vedic puja at home in Delhi NCR or at our sacred Kashi site. Griha Pravesh, Marriage, Dosha Nivaran & more.",
              "url": "https://www.antaryatra.org",
              "logo": "https://www.antaryatra.org/logo512.png",
              "image": "https://www.antaryatra.org/images/og-image.jpg",
              "priceRange": "₹₹",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Varanasi",
                "addressRegion": "Uttar Pradesh",
                "postalCode": "221001",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 25.3176,
                "longitude": 82.9739
              },
              "areaServed": [
                { "@type": "City", "name": "Delhi" },
                { "@type": "City", "name": "Noida" },
                { "@type": "City", "name": "Gurgaon" },
                { "@type": "City", "name": "Varanasi" }
              ],
              "openingHours": "Mo-Su 06:00-20:00",
              "sameAs": [
                "https://www.youtube.com/@antaryatra",
                "https://www.instagram.com/antaryatra"
              ]
            },
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": "Popular Vedic Puja Services by Antaryatra",
              "description": "Authentic Vedic puja ceremonies performed by Kashi-certified Acharyas",
              "url": "https://www.antaryatra.org",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Rudrabhishekh", "url": "https://www.antaryatra.org" },
                { "@type": "ListItem", "position": 2, "name": "Maha Mrityunjaya Pujan", "url": "https://www.antaryatra.org" },
                { "@type": "ListItem", "position": 3, "name": "Grih Pravesh Pujan", "url": "https://www.antaryatra.org" },
                { "@type": "ListItem", "position": 4, "name": "Shubh Vivah", "url": "https://www.antaryatra.org" },
                { "@type": "ListItem", "position": 5, "name": "Shani Shanti Pujan", "url": "https://www.antaryatra.org" },
                { "@type": "ListItem", "position": 6, "name": "Satyanarayan Pujan", "url": "https://www.antaryatra.org" }
              ]
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Can NRIs book a puja to be performed in Kashi?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Antaryatra performs authentic Vedic rituals at our sacred Kashi site on your behalf — you can book from anywhere in the world."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Which areas do you serve for at-home puja?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We serve Delhi NCR for at-home puja services. We also perform pujas at our sacred Kashi (Varanasi) site for devotees who cannot travel."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Are the Acharyas verified?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Every Acharya is Kashi-certified, from a traditional Kashi lineage — verified, trained, and practising for decades."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the starting price for puja services?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Puja services start from ₹12,000 and include curated samagri, muhurat guidance, guided rituals, and post-puja support."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What types of pujas can I book?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We offer Rudrabhishekh, Maha Mrityunjaya Pujan, Grih Pravesh Pujan, Shubh Vivah, Shani Shanti Pujan, Satyanarayan Pujan, Kashi Shradh Karm, Pitra Dosh Nivaran, Tripindi Shradh, Kaal Sarp Dosh Nivaran, and more."
                  }
                }
              ]
            }
          ]}
        />
        <HeroBanner
          onScrollToServices={() => {
            const el = document.getElementById('popular-pujas') || document.getElementById('services');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
        <HowItWorks />
        <IntentSelector />
        <PopularPujas />
        <KashiPujas />
        <YouTubeSection
          title="Real Pujas. Real Families. Real Blessings."
          subtitle="See how our Acharyas perform authentic Vedic rituals across homes"
          videos={homePageVideos}
        />
        <ServiceCards />
      </>
    );
  }

  // Use utility function to determine user type first
  const userType = getUserType(user);
  console.log('🔍 User Type Determined:', userType);

  // Only check provider status if user is actually a provider
  if (userType === 'provider' && isProviderUnderReview()) {
    console.log('✅ Redirecting provider to under-review page');
    return <ProviderUnderReview />;
  }

  // Role-based routing
  switch (userType) {
    case 'admin':
      console.log('✅ Redirecting to admin dashboard');
      return <AdminDashboard />;
    case 'provider':
      console.log('✅ Redirecting to provider dashboard');
      return <ProviderDashboard />;
    case 'customer':
    default:
      console.log('✅ Showing customer homepage');
      // Show homepage for customers and any unknown user types
      return (
        <>
          <SEOHead
            title="Book Kashi Acharyas for Pooja at Home & Kashi Rituals"
            description="Book Kashi-certified Acharyas for authentic Vedic puja at home in Delhi NCR — or have your ritual performed at our sacred Kashi site. Griha Pravesh, Marriage, Dosha Nivaran & more."
            canonicalPath="/"
            keywords="Kashi Acharya booking, book pandit at home Delhi NCR, Griha Pravesh puja pandit, pandit for marriage puja, Dosha Nivaran puja, Rudrabhishekh Kashi, NRI puja Kashi, online puja booking"
            structuredData={{
              "@context": "https://schema.org",
              "@type": ["LocalBusiness", "ReligiousOrganization"],
              "name": "Antaryatra",
              "description": "Book Kashi-certified Acharyas for authentic Vedic puja at home in Delhi NCR or at our sacred Kashi site.",
              "url": "https://www.antaryatra.org",
              "logo": "https://www.antaryatra.org/logo512.png",
              "image": "https://www.antaryatra.org/images/og-image.jpg",
              "priceRange": "₹₹",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Varanasi",
                "addressRegion": "Uttar Pradesh",
                "postalCode": "221001",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 25.3176,
                "longitude": 82.9739
              },
              "areaServed": [
                { "@type": "City", "name": "Delhi" },
                { "@type": "City", "name": "Noida" },
                { "@type": "City", "name": "Gurgaon" },
                { "@type": "City", "name": "Varanasi" }
              ],
              "openingHours": "Mo-Su 06:00-20:00",
              "sameAs": [
                "https://www.youtube.com/@antaryatra",
                "https://www.instagram.com/antaryatra"
              ]
            }}
          />
          <HeroBanner
            onScrollToServices={() => {
              const el = document.getElementById('popular-pujas') || document.getElementById('services');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
          <HowItWorks />
          <IntentSelector />
          <PopularPujas />
          <KashiPujas />
          <YouTubeSection
            title="Real Pujas. Real Families. Real Blessings."
            subtitle="See how our Acharyas perform authentic Vedic rituals across homes"
            videos={homePageVideos}
          />
          <ServiceCards />
        </>
      );
  }
};

export default SmartLanding;